/** Props for {@link Sparkline}. */
export interface SparklineProps {
  /** Series of numeric values to plot. Renders nothing when empty. */
  data: number[];
  /**
   * Stroke/fill color. Accepts any CSS color value.
   * Defaults to `currentColor` so it inherits from a parent's `color`.
   */
  color?: string;
  /** SVG viewport width in pixels. Default 84. */
  width?: number;
  /** SVG viewport height in pixels. Default 28. */
  height?: number;
  /**
   * When `true`, renders a translucent (0.12 opacity) polygon under the
   * polyline to give a filled-area look.
   */
  fill?: boolean;
}

/**
 * Sparkline — inline mini-chart rendered as a plain `<svg>` polyline.
 *
 * Shell: any.
 *
 * Returns `null` when `data` is empty or has fewer than two points (nothing
 * meaningful to draw). The SVG uses `overflow: visible` so the stroke at
 * extreme points is not clipped.
 *
 * Corrected 2026-08-04: this used to read "Mirrors `primitives.jsx` lines
 * 92–120". `primitives.jsx` is one of the ghost sources
 * `mtb-design-system/platforms/alignment.md` §6 names — a design bundle in no
 * repo, quoted with line numbers nobody can check. This component is the
 * governed artifact now (#108).
 */
export function Sparkline({
  data,
  color = 'currentColor',
  width = 84,
  height = 28,
  fill = false,
}: SparklineProps) {
  if (!data || data.length === 0) return null;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;

  const pts = data
    .map(
      (v, i) => `${(i * stepX).toFixed(2)},${(height - ((v - min) / range) * height).toFixed(2)}`,
    )
    .join(' ');

  return (
    <svg width={width} height={height} style={{ overflow: 'visible' }}>
      {fill && (
        <polygon points={`0,${height} ${pts} ${width},${height}`} fill={color} opacity={0.12} />
      )}
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}
